import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";

interface LoadingSplashProps {
  /**
   * Delay in ms before showing the loading screen (default: 200ms)
   */
  readonly delay?: number;
  /**
   * Optional app name or title
   */
  readonly appName?: string;
  /**
   * Optional external progress (0-100). If omitted, fake progress is used.
   */
  readonly progress?: number;
}

// Array of creative German loading messages
const germanLoadingMessages = [
  "Daten werden geladen...",
  "Einen Moment bitte...",
  "Wir optimieren noch das Internet...",
  "Daten werden hübsch gemacht...",
  "Wir organisieren Pixel in Reih und Glied...",
  "Bitte lächeln – Ihre Daten kommen gleich!",
  "Server holt kurz Luft...",
  "Technische Magie passiert gerade...",
  "Inhalte nehmen gerade die letzte Kurve...",
  "Wir lassen Bits und Bytes vortanzen...",
  "Ein kurzer Boxenstopp für Ihre Daten...",
  "Digitaler Feinschliff läuft...",
  "Ihre Anfrage hat Vorfahrt...",
  "Virtuelle Heinzelmännchen am Werk...",
  "Bits werden zurechtgerückt, einen Moment...",
  "Server dreht noch eine Ehrenrunde...",
  "Ihr Wunsch ist dem Server Befehl...",
];

export default function LoadingSplash({
  delay = 200,
  appName = "Application",
  progress: externalProgress,
}: LoadingSplashProps) {
  const [showLoading, setShowLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("");
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const messageIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Function to get a random message
  const getRandomMessage = useCallback(() => {
    const randomIndex = Math.floor(
      Math.random() * germanLoadingMessages.length,
    );
    return germanLoadingMessages[randomIndex] ?? "Loading...";
  }, []);

  const isExternallyControlled = typeof externalProgress === "number";
  const visibleProgress = isExternallyControlled
    ? Math.min(Math.max(externalProgress, 0), 100)
    : progress;

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoading(true);
      // Set initial message
      setLoadingMessage(getRandomMessage());
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, getRandomMessage]);

  useEffect(() => {
    if (showLoading && !isExternallyControlled) {
      // Clear any existing interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }

      // Reset progress
      setProgress(0);

      // Start fake progress simulation with logarithmic curve
      progressIntervalRef.current = setInterval(() => {
        setProgress((prevProgress) => {
          // Logarithmic curve: fast at first, then slows down
          // Never reaches 100% until loading is complete
          if (prevProgress < 90) {
            const remaining = 100 - prevProgress;
            const increment = remaining * 0.1; // Take 10% of remaining each time
            return Math.min(prevProgress + increment, 90);
          }
          return prevProgress;
        });
      }, 200);

      // Change message every few seconds
      messageIntervalRef.current = setInterval(() => {
        setLoadingMessage(getRandomMessage());
      }, 3000);
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
      }
    };
  }, [showLoading, getRandomMessage, isExternallyControlled]);

  return (
    <AnimatePresence>
      {showLoading && (
        <motion.div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            scale: 1.05,
            transition: {
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1], // Custom cubic-bezier for a smooth reveal
            },
          }}
        >
          <div className="flex flex-col items-center justify-center space-y-8 p-4 text-center">
            <div className="space-y-2">
              <h1 className="text-2xl font-bold tracking-tight">
                Lade {appName}
              </h1>
              <div className="h-6">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={loadingMessage}
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                    }}
                    className="text-sm text-muted-foreground"
                  >
                    {loadingMessage}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>

            <div className="w-80">
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-zinc-600 transition-all duration-300 ease-out"
                  style={{ width: `${visibleProgress}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {Math.round(visibleProgress)}%
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
